// Page loads
//   → initAjaxinate()
//   → Engine reads page 2 URL from hidden link
//   → IntersectionObserver watches #AjaxinatePagination
// User scrolls until loader enters view
//   → loadPage() fetches page 2 (one page at a time)
//   → Products appended with data-page-index="2"
//   → Pagination link updated to page 3
//   → Next load only after loader leaves view and re-enters
// User applies a filter
//   → facets.js replaces #ProductGridContainer HTML
//   → onProductGridUpdated()
//   → Fresh engine on new grid, all items tagged page 1
// Last page reached
//   → No more #AjaxinateTargetLink
//   → Observer removed, pagination cleared

function getGridItemLinks(container) {
  return Array.from(container.querySelectorAll(':scope > li'))
    .map(function (li) {
      var link = li.querySelector('a[href]');
      return link ? link.getAttribute('href') : null;
    })
    .filter(Boolean);
}

function CustomAjaxinate(settings) {
  var defaults = { container: '#product-grid', pagination: '#AjaxinatePagination', offset: 300 };
  this.settings = Object.assign({}, defaults, settings);
  this.container = document.querySelector(this.settings.container);
  this.pagination = document.querySelector(this.settings.pagination);
  this.requestPending = false;
  this.intersectionObserver = null;
  this.canTriggerLoad = true;

  if (this.container && this.pagination) {
    this.initialize();
  }
}

CustomAjaxinate.prototype.isPaginationInView = function () {
  if (!this.pagination) return false;

  var rect = this.pagination.getBoundingClientRect();
  var margin = this.settings.offset;

  return rect.top < window.innerHeight + margin && rect.bottom > -margin;
};

CustomAjaxinate.prototype.syncTriggerLatch = function () {
  if (!this.isPaginationInView()) {
    this.canTriggerLoad = true;
  }
};

CustomAjaxinate.prototype.destroy = function () {
  if (this.intersectionObserver) {
    this.intersectionObserver.disconnect();
    this.intersectionObserver = null;
  }
};

CustomAjaxinate.prototype.showLoader = function () {
  var loader = this.pagination.querySelector('.loader');
  var idle = this.pagination.querySelector('.infinite-scroll-loader__idle');

  if (loader) loader.classList.remove('hide-loader');
  if (idle) idle.classList.add('hide-loader');
};

CustomAjaxinate.prototype.hideLoader = function () {
  var loader = this.pagination.querySelector('.loader');
  var idle = this.pagination.querySelector('.infinite-scroll-loader__idle');

  if (loader) loader.classList.add('hide-loader');
  if (idle) idle.classList.remove('hide-loader');
};

CustomAjaxinate.prototype.finishLoading = function () {
  this.requestPending = false;
};

CustomAjaxinate.prototype.initialize = function () {
  this.extendedPagination = this.pagination.querySelector('#AjaxinateTargetLink');
  if (this.extendedPagination) {
    this.nextPageUrl = this.extendedPagination.href;
    this.bindIntersectionObserver();
  }
};

CustomAjaxinate.prototype.bindIntersectionObserver = function () {
  var self = this;

  if (this.intersectionObserver) {
    this.intersectionObserver.disconnect();
  }

  this.intersectionObserver = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) {
          self.canTriggerLoad = true;
          return;
        }

        if (!self.canTriggerLoad || self.requestPending || !self.nextPageUrl) {
          if (!self.nextPageUrl) {
            self.destroy();
          }
          return;
        }

        self.canTriggerLoad = false;
        self.requestPending = true;
        self.loadPage();
      });
    },
    {
      rootMargin: '0px 0px ' + this.settings.offset + 'px 0px',
      threshold: 0,
    }
  );

  this.intersectionObserver.observe(this.pagination);
  this.syncTriggerLatch();
};

CustomAjaxinate.prototype.loadPage = function () {
  var xhr = new XMLHttpRequest();
  var self = this;

  self.showLoader();
  xhr.open('GET', this.nextPageUrl);
  xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
  xhr.onreadystatechange = function () {
    if (xhr.readyState !== 4) return;

    if (xhr.status !== 200) {
      self.hideLoader();
      self.canTriggerLoad = true;
      self.finishLoading();
      return;
    }

    var doc = new DOMParser().parseFromString(xhr.responseText, 'text/html');
    var incomingContainer = doc.querySelector(self.settings.container);
    var incomingPagination = doc.querySelector(self.settings.pagination);
    var existingLinks = getGridItemLinks(self.container);

    if (incomingContainer && incomingContainer.innerHTML.trim() !== '') {
      var parserDiv = document.createElement('div');
      parserDiv.innerHTML = incomingContainer.innerHTML;
      var incomingItems = parserDiv.querySelectorAll(':scope > li');
      var hasDuplicates = false;

      if (incomingItems.length > 0) {
        var firstIncomingLink = incomingItems[0].querySelector('a[href]');
        if (firstIncomingLink && existingLinks.includes(firstIncomingLink.getAttribute('href'))) {
          hasDuplicates = true;
        }
      }

      if (hasDuplicates) {
        self.destroy();
        self.pagination.innerHTML = '';
        self.canTriggerLoad = true;
        self.finishLoading();
        return;
      }

      var urlObj = new URL(self.nextPageUrl, window.location.origin);
      var pageNum = urlObj.searchParams.get('page') || '1';
      Array.from(incomingContainer.children).forEach(function (child) {
        child.setAttribute('data-page-index', pageNum);
      });

      self.container.insertAdjacentHTML('beforeend', incomingContainer.innerHTML);
    }

    updateActivePageIndicator();

    var incomingLink = incomingPagination ? incomingPagination.querySelector('#AjaxinateTargetLink') : null;

    if (!incomingPagination || !incomingLink || incomingLink.href === self.nextPageUrl) {
      self.destroy();
      self.pagination.innerHTML = '';
      self.finishLoading();
      window.dispatchEvent(new Event('scroll'));
      return;
    }

    self.pagination.innerHTML = incomingPagination.innerHTML;
    self.finishLoading();
    self.initialize();
    window.dispatchEvent(new Event('scroll'));
  };
  xhr.onerror = function () {
    self.hideLoader();
    self.canTriggerLoad = true;
    self.finishLoading();
  };
  xhr.send();
};

var ajaxinateInstance = null;
var paginationIndicatorInitialized = false;

window.runAjaxinateEngine = function () {
  var grid = document.getElementById('product-grid');
  var pagination = document.getElementById('AjaxinatePagination');
  if (!grid || !pagination) return;

  if (ajaxinateInstance) {
    ajaxinateInstance.destroy();
    ajaxinateInstance = null;
  }

  ajaxinateInstance = new CustomAjaxinate({
    container: '#product-grid',
    pagination: '#AjaxinatePagination',
    offset: 300,
  });
};

window.onProductGridUpdated = function () {
  tagInitialGridItems();
  window.runAjaxinateEngine();
  updateActivePageIndicator();
};

function tagInitialGridItems() {
  document.querySelectorAll('#product-grid > li').forEach(function (el) {
    el.setAttribute('data-page-index', '1');
  });
}

function getPaginationPageItems() {
  var standardPagination = document.getElementById('AjaxinateStandardPagination');
  var list = standardPagination
    ? standardPagination.querySelector('.pagination__list')
    : null;

  if (!list) {
    var grid = document.getElementById('product-grid');
    if (!grid) return [];

    var scope = grid.closest('.collection, .template-search__results');
    if (!scope) return [];

    list = scope.querySelector('.pagination__list');
  }

  if (!list) return [];

  return Array.from(list.querySelectorAll('.pagination__item')).filter(function (item) {
    return /^\d+$/.test(item.textContent.trim());
  });
}

function updateActivePageIndicator() {
  var items = document.querySelectorAll('#product-grid > li[data-page-index]');
  if (items.length === 0) return;

  var currentActivePage = '1';
  var scrollPosition = window.scrollY || window.pageYOffset;
  var windowHeightHalf = window.innerHeight / 2;

  for (var i = 0; i < items.length; i++) {
    var elementTop = items[i].getBoundingClientRect().top + scrollPosition;
    if (scrollPosition + windowHeightHalf >= elementTop) {
      currentActivePage = items[i].getAttribute('data-page-index');
    }
  }

  var pagerItems = getPaginationPageItems();

  pagerItems.forEach(function (item) {
    var linkText = item.textContent.trim();
    var isActive = linkText === String(currentActivePage);

    item.classList.remove('pagination__item--current', 'light');
    item.removeAttribute('aria-current');

    if (isActive) {
      item.classList.add('pagination__item--current', 'light');
      item.setAttribute('aria-current', 'page');
    }
  });
}

function initPaginationIndicator() {
  if (paginationIndicatorInitialized) return;
  paginationIndicatorInitialized = true;

  window.addEventListener('scroll', updateActivePageIndicator);
  setTimeout(updateActivePageIndicator, 500);
}

function initAjaxinate() {
  tagInitialGridItems();
  window.runAjaxinateEngine();
  initPaginationIndicator();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAjaxinate);
} else {
  initAjaxinate();
}

document.addEventListener('shopify:section:load', function (event) {
  if (event.target.querySelector('#product-grid')) {
    setTimeout(window.onProductGridUpdated, 50);
  }
});
